import React from "react";

import {
    Box, Button, Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider, Grid, IconButton,
    InputAdornment, LinearProgress, Menu, MenuItem, TextField, Tooltip, Typography
} from "@mui/material";

import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import ApplicationContext from "../contexts/application";

import { CustomDataGrid } from "../components/CustomDataGrid";
import { deleteDictionaryItem, getDictionary, getDictionaryCategories, saveDictionaryItems } from "../libs/apiCalls";
import { GridRowEditStopReasons, GridRowModes } from "@mui/x-data-grid";
import { useNavigate } from "react-router";
import { UserRoles } from "../libs/userRoles";
import ConfirmDialog from "../components/ConfirmDialog";

function Lookups() {
    let defaultPageSize = 20;

    const { settings, setDialog, setSnackbar } = React.useContext(ApplicationContext);
    const navigate = useNavigate();

    const [searchString, setSearchString] = React.useState('');
    const [categoryList, setCategoryList] = React.useState([]);
    const [selectedCategory, setSelectedCategory] = React.useState('');
    const [categoryId, setCategoryId] = React.useState('');
    
    const [selectedRow, setSelectedRow] = React.useState({});
    const [gridSelection, setGridSelection] = React.useState([]);
    
    const [rowModesModel, setRowModesModel] = React.useState({});
    const [isDirty, setIsDirty] = React.useState(false);
    const [anchor, setAnchor] = React.useState(null);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(defaultPageSize);
    const open = Boolean(anchor);

    const [openDialog, setOpenDialog] = React.useState(false);
    const [openDelete, setOpenDelete] = React.useState(false);
    const [reload, setReload] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    
    const [gridRows, setGridRows] = React.useState([]);
    const columns = [
        { field: 'id', headerName: 'Id', width: 90 },
        { field: 'source', headerName: 'Source', flex: 0.33 },
        { field: 'value', headerName: 'Value', flex: 0.33 },
        {
            field: 'name',
            headerName: 'Category',
            flex: 0.33,
            renderCell: (data) => { return data.row?.dataCategory?.name },
            filterable: false
        }
    ];

    const [serverData, setServerData] = React.useState({
        records: [],
        currentPage: 0,
        totalPages: 0,
        totalRecords: 0
    });

    const filter = React.useRef({
        searchString: '',
        fromDate: '',
        toDate: '',
        priority: '',
        topic: '',
        status: ''
    });

    const getData = (filter, page, pageSize) => {
        setLoading(true);

        getDictionaryCategories(settings.token,
            (response) => {
                if (!response.data.error) {
                    setCategoryList(JSON.parse(response.data.result));
                }
                else {
                    setDialog(response.data.errors[0], true);
                }

                setLoading(false);
            },
            (error) => {
                setDialog(error.message, true);
                setLoading(false);
            }
        );

        getDictionary(settings.token, JSON.stringify(filter), page, pageSize, 'CategoryId', 0,
            (response) => {
                if (!response.data.error) {
                    setServerData({
                        records: JSON.parse(response.data.result),
                        currentPage: response.data.currentPage,
                        totalPages: response.data.totalPages,
                        totalRecords: response.data.totalRecords
                    });

                    setGridRows(JSON.parse(response.data.result));
                }
                else {
                    setDialog(response.data.errors[0], true);
                }

                setLoading(false);
            },
            (error) => {
                setDialog(error.message, true);
                setLoading(false);
            }
        );
    }

    const saveData = (row) => {
        if (row.id) {            
            row.updatedBy = settings.userName; 
        } 
        else {
            row.createdBy = settings.userName;
        }

        setLoading(true);
        saveDictionaryItems(settings.token, JSON.stringify([row]),
            (response) => {
                if (!response.data.error) {
                    getData(filter.current, page, pageSize);
                    setSnackbar(row.id ? 'Record updated successfully.' : 'Record added successfully.');
                }
                else {
                    setDialog(response.data.errors[0], true);
                }

                setLoading(false);
            },
            (error) => {
                setDialog(error.message, true);
                setLoading(false);
            }
        );
    };

    const handlePageModelChanged = (event) => {
        setPage(event.page);
        setPageSize(event.pageSize);

        getData(filter.current, event.page, event.pageSize);
    };

    const handleFilter = () => {
        getData(filter.current, page, pageSize);
    };

    const handleFilterChange = (event) => {
        filter.current[event.target.name] = event.target.value;

        if (event.target.name === 'topic') {
            setSelectedCategory(event.target.value);
        }
        else if (event.target.name === 'searchString') {
            setSearchString(event.target.value);
        }
    };

    const handleClearFilter = () => {
        filter.current.searchString = '';
        filter.current.fromDate = '';
        filter.current.toDate = '';
        filter.current.priority = '';
        filter.current.topic = '';
        filter.current.status = '';

        setSelectedCategory('');
        setSearchString('');
        getData(filter.current, page, pageSize);
    };

    const handleCancel = () => {
        const newModel = { ...rowModesModel };
        Object.keys(newModel).forEach((item) => newModel[item] = { mode: GridRowModes.View, ignoreModifications: true });

        setRowModesModel(newModel);
        if (isDirty) {
            setIsDirty(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setIsDirty(false);
    }

    const handleAddClick = (event) => {
        setAnchor(event.currentTarget);
    }

    const handleMenuClose = (event) => {
        setAnchor(null);
    };

    const handleNew = () => {
        // clear all properties
        setSelectedRow(Object.keys(selectedRow).reduce((k, v) => {
            k[v] = undefined;
            return k;
        }, {}));

        setOpenDialog(true);
        setAnchor(null);
    };

    const handleImportFile = () => {
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCategoryId('');
    };

    const handleSubmitDialog = (event) => {
        event.preventDefault();

        saveData(selectedRow);
        setSelectedRow({});

        setOpenDialog(false);
        setCategoryId('');
    }

    const handleDelete = () => {
        if (Object.keys(selectedRow).length > 0) {
            setOpenDelete(true);
        }
    };

    const handleEdit = () => {
        if (Object.keys(selectedRow).length > 0) {
            setOpenDialog(true);
        }
    };

    const handleOnChange = (event) => {

        if (event.target.name === 'categoryId') {
            setCategoryId(event.target.value);
            selectedRow.dataCategory = { id: event.target.value };
        }
        else {
            selectedRow[event.target.name] = event.target.value;
        }
    };

    const handleRowSelected = (ids) => {
        setGridSelection(ids);

        if (ids.length > 0) {
            var row = gridRows.find((row) => row.id === ids[0]);
            setSelectedRow(row);
        }
    };

    const handleCellDoubleClick = (params, event) => {
        handleEdit();
    };

    const handleCancelDelete = () => {
        setOpenDelete(false);
    };

    const doDelete = () => {
        setOpenDelete(false);

        if (Object.keys(selectedRow).length > 0) {
            setLoading(true);

            deleteDictionaryItem(settings.token, JSON.stringify(selectedRow),
                (response) => {
                    if (!response.data.error) {
                        setSelectedRow({});
                        setSnackbar('Item deleted successfully.');
                    }
                    else {
                        setDialog(response.data.errors[0], true);
                    }

                    setLoading(false);
                    setSelectedRow({});
                    setReload(!reload);
                },
                (error) => {
                    setDialog(error.message, true);
                    setLoading(false);
                }
            );
        }
    };

    const handleFilterModelChange = (model) => {
        //console.log(model);
    };

    React.useEffect(() => {
        if (Object.keys(settings).length > 0) {
            // TODO: create another protected control
            if (!settings.roles?.includes(UserRoles.OEM.label)) {
                navigate('/');
            }

            getData(filter.current, page, pageSize);
        }
    }, [settings, reload]);

    return (
        <>
            <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', mb: 2 }}>
                    <Box sx={{ width: "50%" }}>
                        <Typography sx={{ marginLeft: '5px', fontWeight: '400', color: 'text.bright', fontSize: '25px' }}>Data Lookups</Typography>
                        <Typography sx={{ marginLeft: '5px', fontWeight: '300', color: 'text.secondary', fontSize: '15px' }}>Define data translation between source and system values.</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: "50%", mb: 4 }}>
                        {isDirty ?
                            <>
                                <Button variant="outlined" color="error" style={{ width: "120px" }} onClick={handleCancel}>Cancel</Button>
                                <Button variant="contained" color="success" style={{ width: "120px", marginLeft: "7px" }} type='submit'>Save</Button>
                            </> :
                            <>
                                <Button variant="outlined" color="error"
                                    style={{ width: "120px" }}
                                    onClick={handleDelete}
                                >
                                    Delete
                                </Button>
                                <Button variant="outlined" style={{ width: "120px", marginLeft: "7px" }} onClick={handleEdit}>Edit</Button>
                                <Button variant="contained" color="success"
                                    style={{ width: "120px", marginLeft: "7px" }}
                                    endIcon={<KeyboardArrowDownIcon />}
                                    onClick={handleAddClick}
                                    disableElevation
                                    aria-haspopup="true"
                                >
                                    Add
                                </Button>
                                <Menu
                                    open={open}
                                    onClose={handleMenuClose}
                                    elevation={0}
                                    anchorEl={anchor}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    sx={{
                                        marginTop: 1
                                    }}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                minWidth: 150,
                                                border: 1,
                                                borderColor: 'divider'
                                            },
                                        }
                                    }}
                                >
                                    <MenuItem onClick={handleNew} disableRipple sx={{ justifyContent: 'flex-end' }}>
                                        Add new item
                                    </MenuItem>
                                    <MenuItem disabled onClick={handleImportFile} disableRipple sx={{ justifyContent: 'flex-end' }}>
                                        Import from file
                                    </MenuItem>
                                </Menu>
                            </>
                        }
                    </Box>
                </Box>
                <Divider sx={{ mb: 4 }} />
                <Container maxWidth="xl">
                    <LinearProgress sx={{ display: loading ? 'display' : 'none' }} />
                    <Grid item gap={2} sx={{ display: 'flex', width: '100%' }}>
                        <TextField
                            variant='outlined'
                            name='searchString'
                            size='small'
                            placeholder='Search for any attribute or value'
                            InputProps={{
                                startAdornment: <InputAdornment position='start'><SearchIcon /></InputAdornment>
                            }}
                            value={searchString}
                            onChange={handleFilterChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleFilter();
                                }
                            }}
                            sx={{ width: '75%' }} />

                        <TextField
                            name='topic'
                            size='small'
                            select
                            sx={{
                                width: '25%',
                                '& .MuiSelect-select span::before': {
                                    content: "'Category'",
                                },
                            }}
                            value={selectedCategory}
                            onChange={handleFilterChange}
                        >
                            <MenuItem key={-1} value=''><em>All</em></MenuItem>
                            {categoryList.map((item, index) => (
                                <MenuItem key={index} value={item.name}>{item.name}</MenuItem>
                            ))};
                        </TextField>

                        <Tooltip title="Apply filter">
                            <IconButton
                                onClick={handleFilter}
                                color='primary'
                                sx={{ mt: -1, ml: -1 }}
                            >
                                <FilterAltIcon fontSize="large" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Clear filters">
                            <IconButton
                                onClick={handleClearFilter}
                                color='primary'
                                sx={{ mt: -1, ml: -1 }}
                            >
                                <FilterAltOffIcon fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item sx={{ display: 'flex', width: '100%', mt: 2 }} >
                        <CustomDataGrid
                            columns={columns}
                            paginationMode='server'
                            rows={gridRows}
                            rowCount={serverData.totalRecords}

                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: defaultPageSize,
                                    },
                                },
                            }}

                            pagination
                            pageSizeOptions={[5, 10, 20, 50]}
                            onPaginationModelChange={handlePageModelChanged}

                            getRowId={(row) => row.id}

                            getRowClassName={(params) =>
                                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                            }

                            onRowSelectionModelChange={handleRowSelected}
                            rowSelectionModel={gridSelection}
                            onCellDoubleClick={handleCellDoubleClick}
                            
                            onFilterModelChange={handleFilterModelChange}
                            autoHeight
                        />
                    </Grid>
                    <Dialog
                        open={openDialog}
                        onClose={handleCloseDialog}
                        maxWidth="sm"
                        fullWidth={true}
                        PaperProps={{
                            component: "form",
                            onSubmit: (handleSubmitDialog)
                        }}
                    >
                        <DialogTitle>Add Item</DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: "flex" }} spacing={2}>
                                <TextField
                                    required
                                    disabled={selectedRow && selectedRow.id}
                                    name='categoryId'
                                    size='small'
                                    select
                                    sx={{
                                        width: '37%',
                                        '& .MuiSelect-select span::before': {
                                            content: "'Category'",
                                        },
                                    }}
                                    value={selectedRow.dataCategory ? selectedRow.dataCategory.id : categoryId}
                                    onChange={handleOnChange}
                                >
                                    {categoryList.map((item, index) => (
                                        <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                    ))};
                                </TextField>
                            </Box>
                            <Box sx={{ display: "flex", mt: 2 }} spacing={2}>
                                <TextField sx={{ mr: 2, flexGrow: 0.15 }}
                                    required
                                    margin="dense"
                                    id="source"
                                    name="source"
                                    label="Source String"
                                    type="text"
                                    defaultValue={Object.keys(selectedRow).includes('source') ? selectedRow.source : ''}
                                    onChange={handleOnChange}
                                    variant="standard"
                                />
                                <TextField sx={{ flexGrow: 1 }}
                                    required
                                    margin="dense"
                                    id="value"
                                    name="value"
                                    label="Translation"
                                    type="text"
                                    defaultValue={Object.keys(selectedRow).includes('value') ? selectedRow.value : ''}
                                    onChange={handleOnChange}
                                    variant="standard"
                                />
                            </Box>

                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Cancel</Button>
                            <Button type="submit" variant="contained">Save</Button>
                        </DialogActions>
                    </Dialog>
                </Container>
                <ConfirmDialog open={openDelete} title={"Confirm Delete"}
                    content={"Are you sure you want to remove this item? (Note: this cannot be undone)"}
                    onNoClick={handleCancelDelete}
                    onYesClick={doDelete}
                    yesNo
                />
            </form>
        </>
    );
}

export default Lookups;